import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  RefreshCw, CheckCircle, XCircle, AlertTriangle,
  Cpu, Mail, Database, Activity, Plus, Trash2, Settings,
} from 'lucide-react';
import { agentMonitoring, agentGmail, adminSettings, agentTickets } from '../../../services/api';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  ArcElement, Title, Tooltip, Legend
);

// ── Constants ─────────────────────────────────────────────────────────────────

const INTENT_LABELS = {
  order_request:     'Order Request',
  tasting_booking:   'Tasting Booking',
  club_change:       'Club Change',
  group_reservation: 'Group Reservation',
  unknown:           'Unknown',
};

const INTENT_COLORS  = ['#0d6efd', '#198754', '#ffc107', '#dc3545', '#6c757d'];
const ESCAL_COLORS   = ['#198754', '#ffc107', '#dc3545'];

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const fmtTs = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

// ── Sub-components ────────────────────────────────────────────────────────────

function HealthBadge({ ok, label, sub }) {
  return (
    <div className="d-flex align-items-center gap-2">
      {ok
        ? <CheckCircle size={16} className="text-success flex-shrink-0" />
        : <XCircle    size={16} className="text-danger flex-shrink-0" />}
      <div>
        <div className="small fw-semibold">{label}</div>
        {sub && <div className="text-muted" style={{ fontSize: 11 }}>{sub}</div>}
      </div>
    </div>
  );
}

function C7LatencyBadge({ latencyMs, ok }) {
  if (!ok) return <span className="badge bg-danger">Error</span>;
  if (latencyMs == null) return null;
  const color = latencyMs < 500 ? 'success' : latencyMs < 1000 ? 'warning' : 'danger';
  return <span className={`badge bg-${color}`}>{latencyMs}ms</span>;
}

// ── AgentMonitoring ──────────────────────────────────────────────────────────

export default function AgentMonitoring() {
  const location = useLocation();
  const [stats,    setStats]    = useState(null);
  const [costs,    setCosts]    = useState(null);
  const [health,   setHealth]   = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // ── Reply templates state ──────────────────────────────────────────────────
  const [templates,    setTemplates]    = useState([]);
  const [tmplName,     setTmplName]     = useState('');
  const [tmplBody,     setTmplBody]     = useState('');
  const [tmplSaving,   setTmplSaving]   = useState(false);
  const [tmplMsg,      setTmplMsg]      = useState('');

  // ── Gmail setup state ──────────────────────────────────────────────────────
  const [inboxes,       setInboxes]       = useState([]);   // monitored inbox list
  const [inboxInput,    setInboxInput]    = useState('');   // new inbox text field
  const [inboxSaving,   setInboxSaving]   = useState(false);
  const [watchLoading,     setWatchLoading]     = useState(false);
  const [watchResults,     setWatchResults]     = useState(null); // array from registerAllWatches
  const [stopLoading,      setStopLoading]      = useState(false);
  const [stopWatchResults, setStopWatchResults] = useState(null); // array from stopAllWatches
  const [setupMsg,      setSetupMsg]      = useState('');
  const [connectLoading, setConnectLoading] = useState(false);

  // ── AI routing settings state ──────────────────────────────────────────────
  const [aiThreshold,      setAiThreshold]      = useState('');    // string, controlled input (0–100)
  const [aiThresholdSaved, setAiThresholdSaved] = useState(0);     // current saved value (decimal)
  const [aiSettingSaving,  setAiSettingSaving]  = useState(false);
  const [aiSettingMsg,     setAiSettingMsg]     = useState('');

  // ── Monitoring data fetch ──────────────────────────────────────────────────

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError('');
    try {
      const [statsRes, costsRes, healthRes, feedbackRes] = await Promise.all([
        agentMonitoring.stats(),
        agentMonitoring.costs(),
        agentMonitoring.health(),
        agentMonitoring.feedback(),
      ]);
      setStats(statsRes.data.data);
      setCosts(costsRes.data.data);
      setHealth(healthRes.data.data);
      setFeedback(feedbackRes.data.data);
    } catch {
      setError('Failed to load monitoring data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Load monitored inboxes from system settings
  const fetchInboxes = useCallback(async () => {
    try {
      const res = await adminSettings.getSystem();
      const raw = res.data.settings?.agent_monitored_inboxes;
      if (raw) {
        const parsed = JSON.parse(raw);
        setInboxes(Array.isArray(parsed) ? parsed.filter(Boolean) : []);
      }
    } catch {
      // Non-fatal — inboxes default to empty
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await agentTickets.getTemplates();
      setTemplates(res.data.data.templates || []);
    } catch { /* non-fatal */ }
  }, []);

  const fetchAiSettings = useCallback(async () => {
    try {
      const res = await agentMonitoring.getSettings();
      const t   = res.data.data.confidenceThreshold ?? 0;
      setAiThresholdSaved(t);
      setAiThreshold(t > 0 ? String(Math.round(t * 100)) : '');
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchInboxes();
    fetchTemplates();
    fetchAiSettings();
  }, [fetchAll, fetchInboxes, fetchTemplates, fetchAiSettings]);

  // ── Template actions ───────────────────────────────────────────────────────

  const handleAddTemplate = async () => {
    if (!tmplName.trim() || !tmplBody.trim()) return;
    setTmplSaving(true);
    setTmplMsg('');
    try {
      const newTemplate = { id: Date.now().toString(), name: tmplName.trim(), body: tmplBody.trim() };
      const updated = [...templates, newTemplate];
      await agentTickets.updateTemplates(updated);
      setTemplates(updated);
      setTmplName('');
      setTmplBody('');
      setTmplMsg('ok:Template saved.');
    } catch {
      setTmplMsg('err:Failed to save template.');
    } finally {
      setTmplSaving(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    setTmplSaving(true);
    setTmplMsg('');
    try {
      const updated = templates.filter((t) => t.id !== id);
      await agentTickets.updateTemplates(updated);
      setTemplates(updated);
      setTmplMsg('ok:Template deleted.');
    } catch {
      setTmplMsg('err:Failed to delete template.');
    } finally {
      setTmplSaving(false);
    }
  };

  // Handle redirect back from Google OAuth — show success message and refresh health
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('gmailConnected')) {
      setSetupMsg('ok:Gmail connected successfully. Add your monitored inboxes below and click Register Watches.');
      fetchAll(true);
    } else if (params.get('gmailError')) {
      const code = params.get('gmailError');
      const msg  = code === 'access_denied'         ? 'Google OAuth access was denied.'
                 : code === 'token_exchange_failed'  ? 'Token exchange failed — check backend logs.'
                 : 'Gmail connection failed.';
      setSetupMsg(`error:${msg}`);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chart data ────────────────────────────────────────────────────────────

  const volumeChartData = {
    labels: (stats?.dailyVolume || []).map((r) => fmtDate(r.date)),
    datasets: [{
      label:           'Tickets',
      data:            (stats?.dailyVolume || []).map((r) => parseInt(r.count)),
      borderColor:     'rgb(13, 110, 253)',
      backgroundColor: 'rgba(13, 110, 253, 0.1)',
      tension:         0.3,
      fill:            true,
    }],
  };

  const volumeChartOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const intentChartData = {
    labels:   (stats?.intentBreakdown || []).map((i) => INTENT_LABELS[i.intentType] || i.intentType || 'Unknown'),
    datasets: [{
      data:            (stats?.intentBreakdown || []).map((i) => parseInt(i.count)),
      backgroundColor: INTENT_COLORS,
      borderWidth:     1,
    }],
  };

  const escalationChartData = {
    labels:   ['Standard', 'Priority', 'Human Only'],
    datasets: [{
      data: (() => {
        const totals = { standard: 0, priority: 0, human_only: 0 };
        (stats?.escalationRatio || []).forEach((r) => {
          totals.standard  += parseInt(r.standard   || 0);
          totals.priority  += parseInt(r.priority   || 0);
          totals.human_only += parseInt(r.human_only || 0);
        });
        return [totals.standard, totals.priority, totals.human_only];
      })(),
      backgroundColor: ESCAL_COLORS,
      borderWidth:     1,
    }],
  };

  const donutOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
    },
    cutout: '65%',
  };

  // ── Gmail setup actions ───────────────────────────────────────────────────

  const handleConnectGmail = async () => {
    setConnectLoading(true);
    try {
      const res = await agentGmail.authUrl();
      window.location.href = res.data.data.url;
    } catch {
      setSetupMsg('error:Failed to generate authorization URL. Check backend logs.');
      setConnectLoading(false);
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Disconnect Gmail? The agent will stop receiving emails until reconnected.')) return;
    try {
      await agentGmail.disconnect();
      setSetupMsg('ok:Gmail disconnected.');
      fetchAll(true);
    } catch {
      setSetupMsg('error:Failed to disconnect Gmail.');
    }
  };

  const handleAddInbox = () => {
    const email = inboxInput.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (inboxes.includes(email)) return;
    setInboxes((prev) => [...prev, email]);
    setInboxInput('');
  };

  const handleRemoveInbox = (email) => {
    setInboxes((prev) => prev.filter((e) => e !== email));
  };

  const handleSaveInboxes = async () => {
    setInboxSaving(true);
    try {
      await adminSettings.updateSystem({ agent_monitored_inboxes: JSON.stringify(inboxes) });
      setSetupMsg('ok:Monitored inboxes saved.');
    } catch {
      setSetupMsg('error:Failed to save inboxes.');
    } finally {
      setInboxSaving(false);
    }
  };

  const handleRegisterWatches = async () => {
    // Save first, then register
    setWatchLoading(true);
    setWatchResults(null);
    setStopWatchResults(null);
    setSetupMsg('');
    try {
      await adminSettings.updateSystem({ agent_monitored_inboxes: JSON.stringify(inboxes) });
      const res = await agentGmail.watch();
      setWatchResults(res.data.data.results || []);
      const allOk = res.data.data.allOk;
      setSetupMsg(allOk ? 'ok:All watches registered successfully.' : 'error:Some watches failed — see results below.');
    } catch (err) {
      setSetupMsg('error:Watch registration failed. Ensure Gmail is connected and inboxes have delegate access.');
    } finally {
      setWatchLoading(false);
    }
  };

  const handleStopWatches = async () => {
    setStopLoading(true);
    setStopWatchResults(null);
    setWatchResults(null);
    setSetupMsg('');
    try {
      const res = await agentGmail.stopWatches();
      setStopWatchResults(res.data.data.results || []);
      const allOk = res.data.data.allOk;
      setSetupMsg(allOk ? 'ok:All watches stopped.' : 'error:Some watches failed to stop — see results below.');
    } catch {
      setSetupMsg('error:Failed to stop watches. Ensure Gmail is connected.');
    } finally {
      setStopLoading(false);
    }
  };

  // ── AI routing settings actions ───────────────────────────────────────────

  const handleSaveAiSettings = async () => {
    const pct   = parseFloat(aiThreshold);
    const value = aiThreshold === '' ? 0 : pct / 100;

    if (aiThreshold !== '' && (isNaN(pct) || pct < 0 || pct > 100)) {
      setAiSettingMsg('error:Enter a percentage between 0 and 100, or leave blank to disable.');
      return;
    }

    setAiSettingSaving(true);
    setAiSettingMsg('');
    try {
      await agentMonitoring.updateSettings({ confidenceThreshold: value });
      setAiThresholdSaved(value);
      setAiSettingMsg(
        value === 0
          ? 'ok:Threshold routing disabled.'
          : `ok:Tickets below ${Math.round(value * 100)}% confidence will be auto-escalated to Human Only.`
      );
    } catch {
      setAiSettingMsg('error:Failed to save settings. Please try again.');
    } finally {
      setAiSettingSaving(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  // ── Derived health values ─────────────────────────────────────────────────

  const gmailOk    = health?.gmail?.connected && !health?.gmail?.expired;
  const c7Ok       = health?.commerce7?.ok;
  const anthropicOk = health?.anthropic?.configured;
  const queueDepth = health?.queue?.waiting ?? '—';

  return (
    <div>
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Agent Monitoring</h4>
          <p className="text-muted small mb-0">System health, volume, cost, and feedback digest</p>
        </div>
        <button
          className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
          onClick={() => fetchAll(true)}
          disabled={refreshing}
        >
          <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between align-items-center">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      {/* ── System Health row ─────────────────────────────────────────────── */}
      <div className="row g-3 mb-4">

        {/* Gmail */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Mail size={16} className="text-muted" />
                <span className="fw-semibold small">Gmail</span>
              </div>
              <HealthBadge
                ok={gmailOk}
                label={health?.gmail?.connected ? 'Connected' : 'Not connected'}
                sub={health?.gmail?.email || (health?.gmail?.connected && !health?.gmail?.expired
                  ? `Expires ${fmtTs(health.gmail.expiresAt)}`
                  : health?.gmail?.expired ? 'Token expired — reconnect required' : undefined)}
              />
              {health?.gmail?.expired && (
                <div className="mt-2">
                  <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>Token Expired</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Commerce7 */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Database size={16} className="text-muted" />
                <span className="fw-semibold small">Commerce7</span>
              </div>
              <HealthBadge
                ok={c7Ok}
                label={c7Ok ? 'API reachable' : 'API error'}
                sub={health?.commerce7?.error}
              />
              {c7Ok && (
                <div className="mt-2">
                  <C7LatencyBadge latencyMs={health.commerce7.latencyMs} ok={c7Ok} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Anthropic */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Cpu size={16} className="text-muted" />
                <span className="fw-semibold small">Anthropic</span>
              </div>
              <HealthBadge
                ok={anthropicOk}
                label={anthropicOk ? 'API key configured' : 'API key missing'}
              />
              {anthropicOk && (
                <div className="mt-2 d-flex flex-column gap-1">
                  {health.anthropic.haikuModel && (
                    <span className="badge bg-light text-dark border" style={{ fontSize: 10 }}>
                      Haiku: {health.anthropic.haikuModel}
                    </span>
                  )}
                  {health.anthropic.sonnetModel && (
                    <span className="badge bg-light text-dark border" style={{ fontSize: 10 }}>
                      Sonnet: {health.anthropic.sonnetModel}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Queue depth */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Activity size={16} className="text-muted" />
                <span className="fw-semibold small">Queue</span>
              </div>
              <div className={`fs-4 fw-bold ${queueDepth > 10 ? 'text-warning' : 'text-dark'}`}>
                {queueDepth}
              </div>
              <div className="text-muted small">waiting jobs</div>
              {health?.queue && (
                <div className="mt-2 d-flex gap-2 flex-wrap" style={{ fontSize: 11 }}>
                  <span className="text-muted">Active: {health.queue.active ?? '—'}</span>
                  <span className="text-muted">Delayed: {health.queue.delayed ?? '—'}</span>
                  {health.queue.failed > 0 && (
                    <span className="text-danger fw-semibold">Failed: {health.queue.failed}</span>
                  )}
                </div>
              )}
              {health?.lastTicket && (
                <div className="text-muted mt-1" style={{ fontSize: 10 }}>
                  Last: {fmtTs(health.lastTicket.updatedAt)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dead letter failures */}
      {health?.recentFailures?.count > 0 && (
        <div className="alert alert-warning py-2 mb-4 d-flex align-items-start gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-1" />
          <div>
            <span className="fw-semibold">{health.recentFailures.count} dead letter entr{health.recentFailures.count === 1 ? 'y' : 'ies'}</span>
            {' '}in logs/agent-failed.log — most recent:{' '}
            <span className="font-monospace small">
              {health.recentFailures.recent.slice(-1)[0]?.error
                || health.recentFailures.recent.slice(-1)[0]?.raw
                || '(see log file)'}
            </span>
          </div>
        </div>
      )}

      {/* ── Gmail Setup card ──────────────────────────────────────────────── */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-light py-2 d-flex align-items-center gap-2">
          <Settings size={14} className="text-muted" />
          <span className="fw-semibold small">Gmail Configuration</span>
        </div>
        <div className="card-body">

          {/* Setup message toast */}
          {setupMsg && (
            <div className={`alert py-2 mb-3 d-flex justify-content-between align-items-center ${
              setupMsg.startsWith('ok:') ? 'alert-success' : 'alert-danger'
            }`}>
              <span className="small">{setupMsg.replace(/^(ok|error):/, '')}</span>
              <button type="button" className="btn-close btn-close-sm" onClick={() => setSetupMsg('')} />
            </div>
          )}

          {/* Connection status row */}
          <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              {gmailOk
                ? <CheckCircle size={16} className="text-success" />
                : <XCircle    size={16} className="text-danger" />}
              <div>
                <div className="small fw-semibold">
                  {health?.gmail?.connected ? `Connected as ${health.gmail.email}` : 'Not connected'}
                </div>
                {health?.gmail?.connected && health?.gmail?.expiresAt && (
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    Token expires {fmtTs(health.gmail.expiresAt)}
                  </div>
                )}
              </div>
            </div>
            <div className="ms-auto d-flex gap-2">
              {!health?.gmail?.connected ? (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleConnectGmail}
                  disabled={connectLoading}
                >
                  {connectLoading
                    ? <><span className="spinner-border spinner-border-sm me-1" />Redirecting…</>
                    : 'Connect Gmail Account'}
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleDisconnectGmail}
                >
                  Disconnect
                </button>
              )}
            </div>
          </div>

          <hr className="my-3" />

          {/* Monitored inboxes */}
          <div className="mb-1 d-flex align-items-center justify-content-between">
            <span className="fw-semibold small">Monitored Inboxes</span>
            {inboxes.length > 0 && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={handleSaveInboxes}
                disabled={inboxSaving}
              >
                {inboxSaving
                  ? <span className="spinner-border spinner-border-sm" />
                  : 'Save'}
              </button>
            )}
          </div>
          <p className="text-muted small mb-3">
            Add each role inbox the agent should monitor (e.g. <code>orders@jeremywineco.com</code>).
            Each must have <strong>delegate access</strong> granted to{' '}
            <strong>{health?.gmail?.email || 'the connected Gmail account'}</strong> in Gmail Settings.
          </p>

          {/* Current inbox list */}
          {inboxes.length === 0 ? (
            <div className="text-muted small fst-italic mb-3">
              No inboxes configured — will watch the connected account's inbox by default.
            </div>
          ) : (
            <ul className="list-group list-group-flush mb-3">
              {inboxes.map((email) => (
                <li key={email} className="list-group-item px-0 py-2 d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <Mail size={13} className="text-muted" />
                    <span className="small">{email}</span>
                  </div>
                  <button
                    className="btn btn-sm btn-link text-danger p-0"
                    onClick={() => handleRemoveInbox(email)}
                    title="Remove inbox"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Add inbox row */}
          <div className="d-flex gap-2 mb-4">
            <input
              type="email"
              className="form-control form-control-sm"
              placeholder="orders@jeremywineco.com"
              value={inboxInput}
              onChange={(e) => setInboxInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddInbox()}
              style={{ maxWidth: 300 }}
            />
            <button
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={handleAddInbox}
              disabled={!inboxInput.trim()}
            >
              <Plus size={13} /> Add
            </button>
          </div>

          {/* Register / Stop Watches buttons */}
          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
            <button
              className="btn btn-sm btn-primary"
              onClick={handleRegisterWatches}
              disabled={watchLoading || stopLoading || !health?.gmail?.connected}
              title={!health?.gmail?.connected ? 'Connect Gmail first' : ''}
            >
              {watchLoading
                ? <><span className="spinner-border spinner-border-sm me-1" />Registering…</>
                : 'Register Watches'}
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleStopWatches}
              disabled={stopLoading || watchLoading || !health?.gmail?.connected}
              title={!health?.gmail?.connected ? 'Connect Gmail first' : 'Stop Gmail notifications for all monitored inboxes'}
            >
              {stopLoading
                ? <><span className="spinner-border spinner-border-sm me-1" />Stopping…</>
                : 'Stop Watches'}
            </button>
          </div>
          <div className="text-muted small mb-3">
            Gmail watches expire every 7 days — re-register weekly to maintain email intake.
          </div>

          {/* Register watch results */}
          {watchResults && watchResults.length > 0 && (
            <div className="mt-2">
              <div className="text-muted small mb-2">Register results:</div>
              <ul className="list-group list-group-flush">
                {watchResults.map((r, i) => (
                  <li key={i} className="list-group-item px-0 py-2">
                    <div className="d-flex align-items-start gap-2">
                      {r.ok
                        ? <CheckCircle size={14} className="text-success flex-shrink-0 mt-1" />
                        : <XCircle    size={14} className="text-danger flex-shrink-0 mt-1" />}
                      <div>
                        <div className="small fw-semibold">{r.userId}</div>
                        {r.ok ? (
                          <div className="text-muted" style={{ fontSize: 11 }}>
                            historyId: {r.historyId} · expires {fmtTs(r.expiration ? parseInt(r.expiration) : null)}
                          </div>
                        ) : (
                          <div className="text-danger" style={{ fontSize: 11 }}>{r.error}</div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Stop watch results */}
          {stopWatchResults && stopWatchResults.length > 0 && (
            <div className="mt-2">
              <div className="text-muted small mb-2">Stop results:</div>
              <ul className="list-group list-group-flush">
                {stopWatchResults.map((r, i) => (
                  <li key={i} className="list-group-item px-0 py-2">
                    <div className="d-flex align-items-start gap-2">
                      {r.ok
                        ? <CheckCircle size={14} className="text-success flex-shrink-0 mt-1" />
                        : <XCircle    size={14} className="text-danger flex-shrink-0 mt-1" />}
                      <div>
                        <div className="small fw-semibold">{r.userId}</div>
                        {!r.ok && (
                          <div className="text-danger" style={{ fontSize: 11 }}>{r.error}</div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </div>

      {/* ── Volume chart ──────────────────────────────────────────────────── */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-light py-2">
          <span className="fw-semibold small">Daily Ticket Volume — Last 30 Days</span>
        </div>
        <div className="card-body">
          {(stats?.dailyVolume || []).length === 0 ? (
            <p className="text-muted text-center py-4 mb-0">No ticket data in the past 30 days.</p>
          ) : (
            <Line data={volumeChartData} options={volumeChartOptions} />
          )}
        </div>
      </div>

      {/* ── Donut charts ──────────────────────────────────────────────────── */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-light py-2">
              <span className="fw-semibold small">Intent Type Breakdown</span>
            </div>
            <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              {(stats?.intentBreakdown || []).length === 0 ? (
                <p className="text-muted small mb-0">No data</p>
              ) : (
                <div style={{ maxWidth: 260, width: '100%' }}>
                  <Doughnut data={intentChartData} options={donutOptions} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-light py-2">
              <span className="fw-semibold small">Escalation Level Breakdown</span>
            </div>
            <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              {(stats?.escalationRatio || []).length === 0 ? (
                <p className="text-muted small mb-0">No data</p>
              ) : (
                <div style={{ maxWidth: 260, width: '100%' }}>
                  <Doughnut data={escalationChartData} options={donutOptions} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Model usage + Cost row ────────────────────────────────────────── */}
      <div className="row g-4 mb-4">

        {/* Model usage */}
        <div className="col-12 col-md-5">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-light py-2">
              <span className="fw-semibold small">Model Usage — Last 30 Days</span>
            </div>
            <div className="card-body py-3">
              {(stats?.modelUsage || []).length === 0 ? (
                <p className="text-muted small mb-0">No draft data available.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="text-muted small fw-semibold">Model</th>
                        <th className="text-muted small fw-semibold text-end">Drafts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.modelUsage.map((m, i) => (
                        <tr key={i}>
                          <td className="small">{m.modelUsed || '—'}</td>
                          <td className="small text-end fw-semibold">{m.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cost estimate */}
        <div className="col-12 col-md-7">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-light py-2">
              <span className="fw-semibold small">Estimated API Cost — Last 30 Days</span>
            </div>
            <div className="card-body py-3">
              <div className="mb-3">
                <span className="fs-3 fw-bold">
                  ${costs?.estimatedTotalCost?.toFixed(4) ?? '—'}
                </span>
                <span className="text-muted small ms-2">USD (estimated)</span>
              </div>

              {/* By model */}
              {(costs?.byModel || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-muted mb-1" style={{ fontSize: 11 }}>BY MODEL</div>
                  {costs.byModel.map((m) => (
                    <div key={m.model} className="d-flex justify-content-between align-items-center small mb-1">
                      <span className="text-capitalize">{m.model}</span>
                      <div className="d-flex gap-3">
                        <span className="text-muted">{m.count} drafts</span>
                        <span className="fw-semibold">${m.cost.toFixed(4)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* By intent */}
              {(costs?.byIntentType || []).length > 0 && (
                <div>
                  <div className="text-muted mb-1" style={{ fontSize: 11 }}>BY INTENT</div>
                  {costs.byIntentType.map((i) => (
                    <div key={i.intentType} className="d-flex justify-content-between align-items-center small mb-1">
                      <span>{INTENT_LABELS[i.intentType] || i.intentType}</span>
                      <span className="fw-semibold">${i.cost.toFixed(4)}</span>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-muted mb-0 mt-3" style={{ fontSize: 10 }}>
                {costs?.ratesNote}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Feedback digest ───────────────────────────────────────────────── */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-light py-2">
          <span className="fw-semibold small">Feedback Digest — Past 7 Days</span>
        </div>
        <div className="card-body">
          {!feedback || feedback.totalTickets === 0 ? (
            <p className="text-muted small mb-0">No feedback data in the past 7 days.</p>
          ) : (
            <div className="row g-3">
              {/* KPIs */}
              <div className="col-12 col-sm-4">
                <div className="text-muted small mb-1">Edit Rate</div>
                <div className={`fs-4 fw-bold ${
                  feedback.editRate > 0.4 ? 'text-danger'
                  : feedback.editRate > 0.2 ? 'text-warning'
                  : 'text-success'
                }`}>
                  {Math.round(feedback.editRate * 100)}%
                </div>
                <div className="text-muted small">{feedback.totalEdited}/{feedback.totalTickets} reviewed</div>
              </div>

              <div className="col-12 col-sm-4">
                <div className="text-muted small mb-1">Avg Edit Distance</div>
                <div className="fs-4 fw-bold">{feedback.avgEditDistance}</div>
                <div className="text-muted small">characters changed (avg)</div>
              </div>

              <div className="col-12 col-sm-4">
                <div className="text-muted small mb-1">Top Edited Intents</div>
                {feedback.topEditedIntents.length > 0 ? (
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {feedback.topEditedIntents.map((intent) => (
                      <span key={intent} className="badge bg-danger" style={{ fontSize: 11 }}>
                        {INTENT_LABELS[intent] || intent}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-success small mt-1">None above 40%</div>
                )}
              </div>

              {/* Recommendation */}
              <div className="col-12">
                <div className="alert alert-light border py-2 mb-0 d-flex align-items-start gap-2">
                  <Activity size={14} className="text-muted flex-shrink-0 mt-1" />
                  <span className="small">{feedback.recommendation}</span>
                </div>
              </div>

              {/* By intent table */}
              {feedback.byIntentType.length > 0 && (
                <div className="col-12">
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="text-muted small fw-semibold">Intent</th>
                          <th className="text-muted small fw-semibold text-end">Reviewed</th>
                          <th className="text-muted small fw-semibold text-end">Edit Rate</th>
                          <th className="text-muted small fw-semibold text-end">Avg Distance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feedback.byIntentType.map((row) => (
                          <tr key={row.intentType}>
                            <td className="small">{INTENT_LABELS[row.intentType] || row.intentType}</td>
                            <td className="small text-end">{row.count}</td>
                            <td className="small text-end">
                              <span className={`fw-semibold ${
                                row.editRate > 0.4 ? 'text-danger'
                                : row.editRate > 0.2 ? 'text-warning'
                                : 'text-success'
                              }`}>
                                {Math.round(row.editRate * 100)}%
                              </span>
                            </td>
                            <td className="small text-end text-muted">{row.avgEditDistance}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Reply Templates ────────────────────────────────────────────────── */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-light py-2 d-flex align-items-center gap-2">
          <Settings size={14} />
          <span className="fw-semibold small">Reply Templates</span>
          <span className="text-muted ms-auto" style={{ fontSize: 12 }}>
            Available to all reps as quick-fill options in the ticket detail view
          </span>
        </div>
        <div className="card-body">

          {tmplMsg && (
            <div className={`alert py-2 mb-3 ${tmplMsg.startsWith('ok:') ? 'alert-success' : 'alert-danger'}`}>
              {tmplMsg.replace(/^(ok|err):/, '')}
            </div>
          )}

          {/* ── AI Routing Settings ──────────────────────────────────────── */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-light py-2 d-flex align-items-center gap-2">
              <Settings size={15} />
              <span className="fw-semibold small">AI Routing</span>
            </div>
            <div className="card-body py-3">

              {/* Confidence threshold */}
              <div className="mb-3">
                <label className="form-label small fw-semibold mb-1">
                  Low-Confidence Escalation Threshold
                </label>
                <p className="text-muted small mb-2" style={{ lineHeight: 1.5 }}>
                  When a draft is generated below this confidence level the ticket is
                  automatically escalated to <strong>Human Only</strong>. Leave blank or set
                  to 0 to disable.
                </p>
                <div className="d-flex align-items-center gap-2" style={{ maxWidth: 260 }}>
                  <div className="input-group input-group-sm">
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      max="100"
                      step="5"
                      placeholder="e.g. 40"
                      value={aiThreshold}
                      onChange={(e) => setAiThreshold(e.target.value)}
                      disabled={aiSettingSaving}
                      style={{ maxWidth: 90 }}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleSaveAiSettings}
                    disabled={aiSettingSaving}
                  >
                    {aiSettingSaving
                      ? <span className="spinner-border spinner-border-sm" />
                      : 'Save'}
                  </button>
                </div>

                {/* Current active value */}
                {aiThresholdSaved > 0 && (
                  <div className="mt-2 d-flex align-items-center gap-1" style={{ fontSize: 12 }}>
                    <CheckCircle size={12} className="text-success" />
                    <span className="text-muted">
                      Active: drafts below <strong>{Math.round(aiThresholdSaved * 100)}%</strong> → Human Only
                    </span>
                  </div>
                )}
                {aiThresholdSaved === 0 && (
                  <div className="mt-2" style={{ fontSize: 12, color: '#888' }}>
                    Threshold routing is currently <strong>disabled</strong>.
                  </div>
                )}
              </div>

              {/* Feedback message */}
              {aiSettingMsg && (
                <div className={`alert py-2 ${aiSettingMsg.startsWith('ok:') ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: 13 }}>
                  {aiSettingMsg.replace(/^(ok|error):/, '')}
                </div>
              )}
            </div>
          </div>

          {/* Existing templates */}
          {templates.length === 0 ? (
            <p className="text-muted small mb-3">No templates yet. Add one below.</p>
          ) : (
            <div className="d-flex flex-column gap-2 mb-4">
              {templates.map((t) => (
                <div key={t.id} className="border rounded p-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-semibold small">{t.name}</span>
                    <button
                      className="btn btn-sm btn-link text-danger p-0"
                      onClick={() => handleDeleteTemplate(t.id)}
                      disabled={tmplSaving}
                      title="Delete template"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <pre
                    className="text-muted small mb-0"
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'inherit',
                      lineHeight: 1.5,
                      maxHeight: 80,
                      overflowY: 'auto',
                    }}
                  >
                    {t.body}
                  </pre>
                </div>
              ))}
            </div>
          )}

          {/* Add new template */}
          <div className="border-top pt-3">
            <p className="fw-semibold small mb-2 d-flex align-items-center gap-1">
              <Plus size={13} /> New Template
            </p>
            <div className="mb-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Template name (e.g. Tasting room hours)"
                value={tmplName}
                onChange={(e) => setTmplName(e.target.value)}
                disabled={tmplSaving}
                maxLength={80}
              />
            </div>
            <div className="mb-2">
              <textarea
                className="form-control form-control-sm font-monospace"
                rows={5}
                placeholder="Template body text…"
                value={tmplBody}
                onChange={(e) => setTmplBody(e.target.value)}
                disabled={tmplSaving}
                style={{ fontSize: 13, resize: 'vertical' }}
              />
            </div>
            <button
              className="btn btn-sm btn-primary d-flex align-items-center gap-1"
              onClick={handleAddTemplate}
              disabled={tmplSaving || !tmplName.trim() || !tmplBody.trim()}
            >
              {tmplSaving
                ? <span className="spinner-border spinner-border-sm" />
                : <><Plus size={13} /> Add Template</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
